import { z } from 'zod';
import type { EventMap } from './events';
import type { DecimalString, IsoTimestamp, Uuid } from './primitives';
import type { EngineStatusResponse } from './dto/engine';
import type { SentimentEventDto } from './dto/sentiment';
import type { NewsItemDto, SymbolVetoDto } from './dto/news';
import type { OrderDto, PositionDto, SignalDto } from './dto/trading';
import type { CloseReason, ExecutionMode } from './enums';

/**
 * Server → client WebSocket protocol (API_DOCS.md — WebSocket Protocol).
 *
 * Envelope: `{ type, ts, payload }`. The union below is discriminated on `type`, so the
 * dashboard gets exhaustive switch handling for free. These are *view* messages: they are
 * derived from EventMap events by the WS layer (throttled, enriched), not the events
 * themselves.
 */

export interface WsEnvelope<T extends WsMessageType, P> {
  readonly type: T;
  readonly ts: IsoTimestamp;
  readonly payload: P;
}

export const WS_MESSAGE_TYPES = [
  'engine.status',
  'signal.detected',
  'signal.rejected',
  'trade.opened',
  'trade.closed',
  'order.update',
  'position.update',
  'pnl.update',
  'sentiment.event',
  'news.item',
  'symbol.veto',
  'risk.circuit_breaker',
  'universe.updated',
  'market.listing',
  'provider.health',
  'log.error',
  'pong',
] as const;
export type WsMessageType = (typeof WS_MESSAGE_TYPES)[number];

export type WsEngineStatus = WsEnvelope<'engine.status', EngineStatusResponse>;
export type WsSignalDetected = WsEnvelope<'signal.detected', SignalDto>;
export type WsSignalRejected = WsEnvelope<'signal.rejected', EventMap['signal.rejected']>;
export type WsTradeOpened = WsEnvelope<'trade.opened', PositionDto>;
export type WsTradeClosed = WsEnvelope<
  'trade.closed',
  {
    readonly tradeId: Uuid;
    readonly symbol: string;
    readonly realizedPnl: DecimalString;
    readonly realizedPnlPct: DecimalString;
    readonly closeReason: CloseReason;
  }
>;
export type WsOrderUpdate = WsEnvelope<'order.update', OrderDto>;
/** Throttled to ≤ 1/s per symbol. */
export type WsPositionUpdate = WsEnvelope<
  'position.update',
  {
    readonly tradeId: Uuid;
    readonly lastPrice: DecimalString;
    readonly unrealizedPnl: DecimalString;
    readonly currentStop: DecimalString | null;
    readonly highWater: DecimalString | null;
  }
>;
/** Throttled to one message per 2s. */
export type WsPnlUpdate = WsEnvelope<
  'pnl.update',
  {
    readonly mode: ExecutionMode;
    readonly equity: DecimalString;
    readonly dailyPnl: DecimalString;
    readonly drawdownPct: DecimalString;
  }
>;
export type WsSentimentEvent = WsEnvelope<'sentiment.event', SentimentEventDto>;
/** Cross-source duplicates are never pushed. */
export type WsNewsItem = WsEnvelope<'news.item', NewsItemDto>;
/** On every veto and on its manual clear (`cleared: true`). */
export type WsSymbolVeto = WsEnvelope<'symbol.veto', SymbolVetoDto & { readonly cleared: boolean }>;
export type WsCircuitBreaker = WsEnvelope<
  'risk.circuit_breaker',
  {
    readonly tripped: boolean;
    readonly drawdownPct: DecimalString;
    readonly limitPct: DecimalString;
  }
>;
export type WsUniverseUpdated = WsEnvelope<'universe.updated', EventMap['universe.updated']>;
/** A new Binance USDT market — upcoming, opened, or removed. */
export type WsMarketListing = WsEnvelope<'market.listing', EventMap['market.listing']>;
export type WsProviderHealth = WsEnvelope<
  'provider.health',
  EventMap['provider.health.changed']
>;
/** Rate-limited by the WS layer. */
export type WsLogError = WsEnvelope<
  'log.error',
  { readonly module: string; readonly message: string }
>;
export type WsPong = WsEnvelope<'pong', Record<string, never>>;

export type ServerWsMessage =
  | WsEngineStatus
  | WsSignalDetected
  | WsSignalRejected
  | WsTradeOpened
  | WsTradeClosed
  | WsOrderUpdate
  | WsPositionUpdate
  | WsPnlUpdate
  | WsSentimentEvent
  | WsNewsItem
  | WsSymbolVeto
  | WsCircuitBreaker
  | WsUniverseUpdated
  | WsMarketListing
  | WsProviderHealth
  | WsLogError
  | WsPong;

/**
 * Client → server: only `ping`. Anything else is untrusted input and is rejected — the
 * socket is a read model, all mutations go through the authenticated REST API.
 */
export const clientWsMessageSchema = z.object({ type: z.literal('ping') });
export type ClientWsMessage = z.infer<typeof clientWsMessageSchema>;

/** Sockets silent for longer than this are disconnected (API_DOCS.md). */
export const WS_IDLE_TIMEOUT_MS = 30_000;
